import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
    ContractDocument,
    ContractSuppliersInfoError,
    RegionRf,
} from '../../types'

import {
    Contract,
    ContractConfirmingDoc,
    ContractDoc,
    ContractSupplier,
    CountryList,
} from '../../../../types'
import LoadingIcon from '../../../../../../../components/LoadingIcon'

import ContractDocumentFileUploaderBlock from './ContractDocumentFileUploaderBlock'
import { Slide, ToastContainer } from 'react-toastify'

import HoverTooltip from '../../../../../../../components/HoverTooltip'
import ContractConfirmingDocsTableRow from './ContractConfirmingDocsTableRow'
import ContractSuppliersTableRow from './ContractSuppliersTableRow'

import ContractSupplierEditPopup from '../ContractSupplierEditPopup'

import ContractSupplierSelectPopup from '../ContractSupplierSelectPopup'

import { isSameContractSupplier, getContractSupplierRowKey } from '../../index'

type Props = {
    theme: string
    contractAction: boolean
    contractGuid: string | undefined
    contract: Contract
    errors?: ContractSuppliersInfoError
    contractSuppliers: ContractSupplier[]
    onDeleteSupplier: (rowKey: string) => void
    onPatch: (guid: string, patch: Partial<ContractSupplier>) => void
    onAddSupplier: (supplier: ContractSupplier) => void
    oksmList: CountryList[]
    regionRfList: RegionRf[]
}

const EMPTY_FORM: ContractSupplier = {
    guid: null,
    supplier_name: null,
    supplier_short_name: null,
    supplier_brand_name: null,
    supplier_additional_full_name: null,
    supplier_inn: null,
    supplier_kpp: null,
    supplier_okpo: null,
    supplier_code: null,
    supplier_additional_code: null,
    supplier_additional_info: null,
    supplier_type: null,
    supplier_provider: null,
    supplier_provider_code: null,
    supplier_subcontractor: null,
    supplier_subcontractor_code: null,
    supplier_individual: null,
    supplier_non_resident: null,
    supplier_registration_date: null,
    supplier_tax: null,
    supplier_okopf: null,
    supplier_okopf_name: null,
    supplier_address_egrul_egrip_changed: null,
    supplier_address_country_name: null,
    supplier_address_digital_code: null,
    supplier_address_post_code: null,
    supplier_address_oktmo: null,
    supplier_address_oktmo_name: null,
    supplier_address_region_code: 0,
    supplier_address_region_name: null,
    supplier_address_area: null,
    supplier_address_city: null,
    supplier_address_settlement: null,
    supplier_address_street: null,
    supplier_address_email: null,
    supplier_address_phone: null,
    supplier_address_office: null,
    supplier_address_building: null,
    supplier_address_house: null,
    supplier_address_planning_structure: null,
    supplier_address_admin_division: null,
    supplier_provider_include_msp_date: null,
    supplier_is_in_order_173n: null,
    supplier_self_employed: null,
    annotation: null,
    organization_guid: null,
    contract_guid: null,
    supplier_guid: null,
    sys_created_at: null,
    sys_updated_at: null,
}

const ContractSuppliersTable: React.FC<Props> = (props) => {
    const [isLoadingContractConfirmingDocs, setLoadingContractConfirmingDocs] =
        useState(false)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [contractSupplierAddPopup, setContractSupplierAddPopup] =
        useState<boolean>(false)
    const [contractSupplierEditPopup, setContractSupplierEditPopup] =
        useState<boolean>(false)

    const [contractSupplierDeleteRowPopup, setContractSupplierDeleteRowPopup] =
        useState<boolean>(false)

    const [deleteRowGuid, setDeleteRowGuid] = useState<string | null>(null)
    const [editRowIndex, setEditRowIndex] = useState<number | null>(null)
    const [isDeleting, setDeleting] = useState<boolean>(false)

    const handleContractSupplierDeleteRowPopup = () => {
        setContractSupplierDeleteRowPopup(!contractSupplierDeleteRowPopup)
    }

    const handleDeleteClick = async () => {
        try {
            setDeleting(true)

            if (deleteRowGuid === null) return

            props.onDeleteSupplier(deleteRowGuid)

            setDeleteRowGuid(null)
            setContractSupplierDeleteRowPopup(false)
        } catch (error) {
            console.error('Error while deleting contract supplier:', error)
        } finally {
            setDeleting(false)
        }
    }

    const handleSelectSupplier = (supplier: ContractSupplier) => {
        const exists = props.contractSuppliers.some(
            (item) =>
                (!!item.supplier_guid &&
                    !!supplier.guid &&
                    item.supplier_guid === supplier.guid) ||
                (!!item.supplier_inn &&
                    !!supplier.supplier_inn &&
                    item.supplier_inn === supplier.supplier_inn)
        )

        if (exists) {
            setContractSupplierAddPopup(false)
            return
        }

        props.onAddSupplier(supplier)
        setContractSupplierAddPopup(false)
    }

    return (
        <>
            <fieldset disabled={false} className="flex w-full h-full">
                <HoverTooltip tooltipText={props.errors?.have_supplier || ''}>
                    <div
                        className={`flex flex-col w-full bg-[#e4e6e8] dark:bg-[#242225] rounded ${
                            props.errors?.have_supplier
                                ? 'border border-[#C15C5C]'
                                : ''
                        }`}
                    >
                        {/* toolbar */}
                        <div className="flex flex-row gap-1 p-2 w-full h-[40px] text-[11px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                            {!props.contractAction && (
                                <div className="flex w-8 h-full items-center justify-center text-center">
                                    <button
                                        className="w-6 h-6 grid rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        onClick={() =>
                                            setContractSupplierAddPopup(true)
                                        }
                                    >
                                        <HoverTooltip tooltipText="Добавить">
                                            <svg
                                                className="w-4 h-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill={
                                                        props.theme === 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                    }
                                                    d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                />
                                            </svg>
                                        </HoverTooltip>
                                    </button>
                                </div>
                            )}

                            <div className="flex flex-1 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                        </div>

                        {/* header */}
                        <div className="flex flex-row gap-1 w-full h-[40px] p-[5px] items-center justify-center text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                            <div className="flex w-[40px] h-full items-center justify-center text-center">
                                №
                            </div>
                            <div className="flex flex-1 h-full items-center justify-center text-center">
                                Наименование контрагента
                            </div>
                            <div className="flex w-[120px] h-full items-center justify-center text-center">
                                ИНН
                            </div>
                            <div className="flex flex-1 h-full items-center justify-center text-center">
                                Адрес
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                        </div>

                        {/* rows */}
                        <div className="flex flex-col w-full gap-1 my-1">
                            {isLoadingContractConfirmingDocs ? (
                                <div className="flex w-full h-full items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            ) : props.contractSuppliers.length > 0 ? (
                                props.contractSuppliers.map((row, index) => {
                                    const rowKeyIndex =
                                        getContractSupplierRowKey(row) ||
                                        String(index)

                                    return (
                                        <ContractSuppliersTableRow
                                            key={rowKeyIndex}
                                            index={index}
                                            contractAction={
                                                props.contractAction
                                            }
                                            contractGuid={props.contractGuid}
                                            theme={props.theme}
                                            contractSupplier={row}
                                            onDeleteSupplierRow={(rowKey) => {
                                                setDeleteRowGuid(rowKey)
                                                handleContractSupplierDeleteRowPopup()
                                            }}
                                            onPatch={props.onPatch}
                                            oksmList={props.oksmList}
                                            regionRfList={props.regionRfList}
                                            errors={
                                                props.errors?.suppliers?.[
                                                    index + 1
                                                ]
                                            }
                                        />
                                    )
                                })
                            ) : (
                                <div className="flex w-full h-full py-1 items-center justify-center text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    Нет данных
                                </div>
                            )}
                        </div>

                        {/* footer */}
                        <div className="flex flex-row gap-1 w-full h-[40px] text-[11px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                            <div className="flex flex-1 gap-3 px-5 h-full items-center justify-start text-center">
                                {!props.contractAction && (
                                    <button
                                        className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                        onClick={() =>
                                            setContractSupplierAddPopup(true)
                                        }
                                        disabled={false}
                                    >
                                        <svg
                                            className="w-3 h-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 22 22"
                                            id="add_supplier"
                                        >
                                            <path
                                                fill={`${props.theme == 'dark' ? '#FFFFFF' : '#181818'}`}
                                                d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                            ></path>
                                        </svg>
                                        Добавить
                                    </button>
                                )}
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                            <div className="flex w-8 h-full items-center justify-center text-center" />
                        </div>
                    </div>
                </HoverTooltip>
            </fieldset>

            {/* delete popup */}
            {contractSupplierDeleteRowPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить контрагента?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractSupplierDeleteRowPopup
                                    }
                                    disabled={isDeleting}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDeleteClick}
                                >
                                    {isDeleting ? '...' : 'Удалить'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ContractSupplierSelectPopup
                open={contractSupplierAddPopup}
                theme={props.theme}
                onClose={() => setContractSupplierAddPopup(false)}
                onSelect={handleSelectSupplier}
                oksmList={props.oksmList}
                regionRfList={props.regionRfList}
                contractGuid={props.contract.guid}
                organizationGuid={props.contract.organization_guid}
                contractAction={props.contractAction}
            />
        </>
    )
}

export default ContractSuppliersTable
